import React, { useRef, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Typography } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PaletteIcon from "@mui/icons-material/Palette";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LayersIcon from "@mui/icons-material/Layers";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BrushIcon from "@mui/icons-material/Brush";
import CodeIcon from "@mui/icons-material/Code";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import GavelIcon from "@mui/icons-material/Gavel";
import SchoolIcon from "@mui/icons-material/School";
import TuneIcon from "@mui/icons-material/Tune";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement | null>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type EnterpriseFeaturesT = {
  label: string;
  icon: React.ReactNode;
};

const fadeIn = (inView: boolean, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(32px)",
  transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
});

const EnterpriseContent = () => {
  const { t }: { t: TranslateFnT } = useTranslation("static_enterprise");
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref: valuePropRef, inView: valuePropInView } = useInView();
  const { ref: flexibilityRef, inView: flexibilityInView } = useInView();
  const { ref: projectRef, inView: projectInView } = useInView();
  const { ref: closeRef, inView: closeInView } = useInView();
  const { ref: featuresRef, inView: featuresInView } = useInView();
  const { ref: paymentsRef, inView: paymentsInView } = useInView();
  const { ref: trainingRef, inView: trainingInView } = useInView();
  const { ref: ctaRef, inView: ctaInView } = useInView();

  const enterpriseFeatures: EnterpriseFeaturesT[] = [
    { label: t("features.items.customTeam"), icon: <AccountTreeIcon /> },
    { label: t("features.items.brandedSeller"), icon: <PaletteIcon /> },
    { label: t("features.items.advancedReporting"), icon: <AssessmentIcon /> },
    { label: t("features.items.multiTierComp"), icon: <LayersIcon /> },
    { label: t("features.items.crmLeadPipeline"), icon: <PeopleIcon /> },
    { label: t("features.items.automatedProposal"), icon: <DescriptionIcon /> },
    { label: t("features.items.integratedFinancing"), icon: <AccountBalanceIcon /> },
    { label: t("features.items.dedicatedAccountMgmt"), icon: <ManageAccountsIcon /> },
    { label: t("features.items.whiteLabel"), icon: <BrushIcon /> },
    { label: t("features.items.apiAccess"), icon: <CodeIcon /> },
    { label: t("features.items.prioritySupport"), icon: <HeadsetMicIcon /> },
    { label: t("features.items.compliance"), icon: <GavelIcon /> },
  ];

  const compensationCards = [
    {
      icon: <TuneIcon sx={{ fontSize: 22, color: "#51F2CB" }} />,
      title: t("compensation.cards.card1.title"),
      description: t("compensation.cards.card1.description"),
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 22, color: "#51F2CB" }} />,
      title: t("compensation.cards.card2.title"),
      description: t("compensation.cards.card2.description"),
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 22, color: "#51F2CB" }} />,
      title: t("compensation.cards.card3.title"),
      description: t("compensation.cards.card3.description"),
    },
  ];

  const projectPills = [
    { label: t("projectManagement.pills.leadEntry"), icon: <PeopleIcon sx={{ fontSize: 20, color: "#51F2CB" }} /> },
    {
      label: t("projectManagement.pills.proposals"),
      icon: <DescriptionIcon sx={{ fontSize: 20, color: "#51F2CB" }} />,
    },
    {
      label: t("projectManagement.pills.financing"),
      icon: <AccountBalanceIcon sx={{ fontSize: 20, color: "#51F2CB" }} />,
    },
    {
      label: t("projectManagement.pills.installationCoordination"),
      icon: <AccountTreeIcon sx={{ fontSize: 20, color: "#51F2CB" }} />,
    },
  ];

  const closeDealsCards = [
    {
      title: t("closeDeals.cards.ownTeam.title"),
      body: t("closeDeals.cards.ownTeam.body"),
      icon: <GroupsIcon sx={{ fontSize: 24, color: "#51F2CB" }} />,
    },
    {
      title: t("closeDeals.cards.platformNetwork.title"),
      body: t("closeDeals.cards.platformNetwork.body"),
      icon: <PeopleIcon sx={{ fontSize: 24, color: "#51F2CB" }} />,
    },
  ];

  const paymentPills = [
    { label: t("payments.pills.automated"), icon: <AutorenewIcon sx={{ fontSize: 20, color: "#51F2CB" }} /> },
    { label: t("payments.pills.accurate"), icon: <VerifiedIcon sx={{ fontSize: 20, color: "#51F2CB" }} /> },
    { label: t("payments.pills.onTime"), icon: <AccessTimeIcon sx={{ fontSize: 20, color: "#51F2CB" }} /> },
  ];

  return (
    <Box>
      <Box sx={styles.heroSection}>
        <Box
          component="iframe"
          src="https://player.vimeo.com/video/1181694765?h=371301da59&background=1&autoplay=1&loop=1&muted=1"
          allow="autoplay; fullscreen"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "177.78vh",
            minWidth: "100%",
            height: "56.25vw",
            minHeight: "100%",
            transform: "translate(-50%, -50%)",
            border: "none",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box sx={styles.heroFlatOverlay} />
        <Box sx={styles.heroRadialOverlay} />
        <Box sx={styles.heroTopGradient} />
        <Box sx={styles.heroBottomGradient} />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>

          <Typography variant="h1" sx={styles.heroHeading}>
            {t("hero.headlineWhite")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>{" "}
            {t("hero.headlineSuffix")}
          </Typography>

          <Typography sx={styles.heroSubheading}>{t("hero.subheading")}</Typography>

          <Box sx={{ animation: "fadeUp 0.7s ease 0.55s both" }}>
            <Button
              component={Link}
              to={`${prefix}/enterprise/signup`}
              variant="contained"
              size="large"
              sx={styles.ctaButton}
            >
              {t("hero.cta")}
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.valuePropSection}>
        <Box ref={valuePropRef} sx={{ ...styles.valuePropTextWrapper, ...fadeIn(valuePropInView) }}>
          <Typography sx={styles.eyebrow}>{t("valueProp.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.sectionHeadingLarge}>
            {t("valueProp.headingLine1")}
            <br />
            <Box component="span" sx={styles.gradientText}>
              {t("valueProp.headingGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.sectionBodyText}>{t("valueProp.body")}</Typography>
        </Box>
        <Box sx={styles.valuePropImageWrapper}>
          <Box
            component="img"
            src="/img/assets/Ipad-enterprise.png"
            alt={t("valueProp.imageAlt")}
            sx={styles.valuePropImage}
          />
        </Box>
      </Box>

      <Box sx={styles.flexibilitySection}>
        <Box sx={styles.flexibilityVideoWrapper}>
          <Box
            component="video"
            autoPlay
            muted
            loop
            playsInline
            src="/img/assets/Enterprise-video2.mp4"
            sx={styles.flexibilityVideo}
          />
        </Box>
        <Box sx={styles.flexibilityTextWrapper}>
          <Box ref={flexibilityRef} sx={{ ...fadeIn(flexibilityInView) }}>
            <Typography sx={styles.eyebrow}>{t("compensation.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.compensationHeading}>
              {t("compensation.headingWhite")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("compensation.headingGradient")}
              </Box>{" "}
              {t("compensation.headingSuffix")}
            </Typography>
            <Typography sx={styles.compensationBody}>{t("compensation.body")}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {compensationCards.map((card, i) => (
                <Box key={card.title} sx={{ width: { xs: "100%", sm: i === 2 ? "100%" : "calc(50% - 12px)" } }}>
                  <Box sx={styles.compensationCard(flexibilityInView, 0.1 + i * 0.1)}>
                    <Box sx={styles.compensationCardGlow(i)} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "auto" }}
                    >
                      <Typography sx={styles.compensationCardNumber}>0{i + 1}</Typography>
                      <Box sx={styles.compensationCardIconBox}>{card.icon}</Box>
                    </Box>
                    <Box sx={{ mt: 4 }}>
                      <Typography sx={styles.compensationCardTitle}>{card.title}</Typography>
                      <Typography sx={styles.compensationCardDescription}>{card.description}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.projectManagementSection}>
        <Container maxWidth="md">
          <Box ref={projectRef} sx={{ textAlign: "center", ...fadeIn(projectInView) }}>
            <Typography sx={styles.eyebrow}>{t("projectManagement.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.sectionHeadingMedium}>
              {t("projectManagement.headingWhite")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("projectManagement.headingGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.sectionBodyCentered}>
              <Box component="span" sx={{ display: "block", maxWidth: 650, mx: "auto" }}>
                {t("projectManagement.body")}
              </Box>
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
              {projectPills.map((item) => (
                <Box key={item.label} sx={styles.pillChip}>
                  <Box sx={styles.pillChipGlow} />
                  {item.icon}
                  <Typography sx={styles.pillChipLabel}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <Button component={Link} to={`${prefix}/enterprise/signup`} variant="contained" sx={styles.ctaButton}>
                {t("projectManagement.cta")}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.closeDealsSection}>
        <Box sx={styles.closeDealsOverlay} />
        <Box sx={styles.closeDealsTopGradient} />
        <Box sx={styles.closeDealsBottomGradient} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box ref={closeRef} sx={{ textAlign: "left", mb: 6, maxWidth: 600, ...fadeIn(closeInView) }}>
            <Typography sx={styles.eyebrow}>{t("closeDeals.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.closeDealsHeading}>
              {t("closeDeals.headingWhite")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("closeDeals.headingGradient")}
              </Box>
            </Typography>
          </Box>
          <Typography sx={styles.closeDealsBody(closeInView)}>{t("closeDeals.body")}</Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, maxWidth: 700 }}>
            {closeDealsCards.map((card, i) => (
              <Box key={card.title} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={styles.closeDealsCard(closeInView, 0.15 + i * 0.1)}>
                  <Box sx={styles.closeDealsCardGlow(i)} />
                  <Box sx={styles.closeDealsCardIconBox}>{card.icon}</Box>
                  <Typography sx={styles.closeDealsCardTitle}>{card.title}</Typography>
                  <Typography sx={styles.closeDealsCardBody}>{card.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={styles.featuresSection}>
        <Container maxWidth="xl">
          <Box ref={featuresRef} sx={{ textAlign: "center", mb: 8, ...fadeIn(featuresInView) }}>
            <Typography sx={styles.eyebrow}>{t("features.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.featuresHeading}>
              {t("features.headingWhite")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("features.headingGradient")}
              </Box>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: { xs: 2, md: 3 }, alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                {enterpriseFeatures.slice(0, 6).map((feature, i) => (
                  <Box key={feature.label} sx={{ width: i === 0 || i === 5 ? "100%" : "calc(50% - 10px)" }}>
                    <Box sx={styles.featureCard(featuresInView, 0.03 * i)}>
                      {[0, 3].includes(i) && <Box sx={styles.featureCardGlowTopLeft} />}
                      {[1, 4].includes(i) && <Box sx={styles.featureCardGlowBottomRight} />}
                      {i === 5 && <Box sx={styles.featureCardGlowTopRight} />}
                      <Box sx={styles.featureCardIconBox}>
                        {React.cloneElement(feature.icon as React.ReactElement, {
                          sx: { fontSize: 20, color: "#51F2CB" },
                        })}
                      </Box>
                      <Typography sx={styles.featureCardLabel}>{feature.label}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={styles.featuresMockupWrapper}>
              <Box
                component="img"
                src="/img/assets/Enterprise-mockup-2.png"
                alt={t("features.mockupAlt")}
                sx={styles.featuresMockupImage}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                {enterpriseFeatures.slice(6).map((feature, i) => (
                  <Box key={feature.label} sx={{ width: i === 0 || i === 5 ? "100%" : "calc(50% - 10px)" }}>
                    <Box sx={styles.featureCard(featuresInView, 0.03 * (i + 6))}>
                      {[0, 3].includes(i) && <Box sx={styles.featureCardGlowTopRight} />}
                      {[1, 4].includes(i) && <Box sx={styles.featureCardGlowBottomLeft} />}
                      {i === 5 && <Box sx={styles.featureCardGlowTopLeft} />}
                      <Box sx={styles.featureCardIconBox}>
                        {React.cloneElement(feature.icon as React.ReactElement, {
                          sx: { fontSize: 20, color: "#51F2CB" },
                        })}
                      </Box>
                      <Typography sx={styles.featureCardLabel}>{feature.label}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.paymentsSection}>
        <Container maxWidth="md">
          <Box ref={paymentsRef} sx={{ textAlign: "center", ...fadeIn(paymentsInView) }}>
            <Typography sx={styles.eyebrow}>{t("payments.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.paymentsHeading}>
              {t("payments.headingWhite")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("payments.headingGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.paymentsBody}>
              <Box component="span" sx={{ display: "block", maxWidth: 650, mx: "auto" }}>
                {t("payments.body")}
              </Box>
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
              {paymentPills.map((item) => (
                <Box key={item.label} sx={styles.pillChip}>
                  <Box sx={styles.pillChipGlow} />
                  {item.icon}
                  <Typography sx={styles.pillChipLabel}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.trainingSection}>
        <Container maxWidth="lg">
          <Box sx={styles.trainingLayout}>
            <Box sx={styles.trainingImageWrapper}>
              <Box
                component="img"
                src="/img/assets/Ipad-enterprise-2.png"
                alt={t("training.imageAlt")}
                sx={styles.trainingImage}
              />
            </Box>

            <Box ref={trainingRef} sx={{ flex: 1, pt: { xs: 0, md: "48px" }, ...fadeIn(trainingInView) }}>
              <Typography sx={styles.eyebrow}>{t("training.eyebrow")}</Typography>
              <Typography variant="h2" sx={styles.trainingHeading}>
                {t("training.headingWhite")}{" "}
                <Box component="span" sx={styles.gradientText}>
                  {t("training.headingGradient")}
                </Box>
              </Typography>
              <Typography sx={styles.trainingBody}>{t("training.body")}</Typography>
              <Box sx={styles.trainingCard(trainingInView)}>
                <Box sx={styles.pillChipGlow} />
                <Box sx={styles.trainingCardIconBox}>
                  <SchoolIcon sx={{ fontSize: 22, color: "#51F2CB" }} />
                </Box>
                <Typography sx={styles.trainingCardText}>{t("training.cardText")}</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.ctaSection}>
        <Box sx={styles.ctaBackgroundImage} />
        <Box sx={styles.ctaVignetteOverlay} />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
          <Box ref={ctaRef} sx={{ textAlign: "center", ...fadeIn(ctaInView) }}>
            <Typography sx={{ ...styles.eyebrow, ...fadeIn(ctaInView, 0.1) }}>{t("cta.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.ctaHeading(ctaInView)}>
              {t("cta.headingWhite")}
              <br />
              <Box component="span" sx={styles.gradientText}>
                {t("cta.headingGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.ctaBody(ctaInView)}>{t("cta.body")}</Typography>
            <Box sx={fadeIn(ctaInView, 0.25)}>
              <Button
                component={Link}
                to={`${prefix}/enterprise/signup`}
                variant="contained"
                size="large"
                sx={styles.ctaButton}
              >
                {t("cta.button")}
              </Button>
            </Box>
            <Typography sx={styles.ctaDisclaimer(ctaInView)}>{t("cta.disclaimer")}</Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const EnterprisePage = () => {
  const { t } = useTranslation("static_enterprise");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl={t("meta.canonicalUrl")}
      ogTitle={t("meta.ogTitle")}
      noTopSpacer
    >
      <EnterpriseContent />
    </StaticPageLayout>
  );
};

export default EnterprisePage;
